import {
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  OnChanges,
  OnInit,
  EventEmitter
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BaseComponent } from "src/app/shared/base.component";
import { NeutModel, NeutUtils, VOORSPONNING_SIZE_MAP, VorsponningSize } from "src/app/shared/models/neut.model";
import { FormComponent } from "src/app/shared/form-component";
import { NeutInput } from "./neut-input";
import { NeutPosition } from "./neut-position";
import { UiStateService } from "src/app/core/services/ui-state-service";
import { filter, takeUntil } from "rxjs/operators";
import { BestellingNeutDto, NeutDto } from "../../shared/api-models/models";
import { GraphicsService } from "../../core/services/graphics.service";
import { TooltipService } from "../../core/services/tooltip.service";
import { DorpelUtils } from "src/app/shared/models/dorpel-type.model";

const DEFAULT_BUITENDORPEL_VOORSPONNING_VALUE = 6;

const MISSING_NEUT_IMAGE_TEXT = "Er is geen tekening voor deze neut.";
const NEUT_VOORSPONNING_ENABLED_KEYWORD = "Ja";
const INCOMPATIBLE_NEUT_AUTOREMOVED_CUSTOM_MESSAGE = "Kies hier een nieuwe neut, de neut die u hier geselecteerd heeft kan niet hier niet geplaats worden in combinatie met de neut aan de linkerzijde van deze dagmaat."

type NeutenCathegory = {
  description: string,
  options: NeutDto[]
}

@Component({
  selector: "luv-neut",
  templateUrl: "./neut-horiz.component.html",
  styleUrls: ["./neut.component.scss"],
})
export class NeutComponent extends BaseComponent implements OnInit, OnChanges {
  public formGroup: FormGroup;
  public neutModel: NeutModel;
  public NeutPosition = NeutPosition;
  public leftVoorsponningOptions: string[] = [];
  public rightVoorsponningOptions: string[] = [];
  public customNeutRemovalErrorMessage: string | null = null;
  private currentlySelectedNeut: BestellingNeutDto;
  private isTooltipResourceCheckInProgress = false;
  private shouldAbortTooltipDisplay = false;

  @Input() public input: NeutInput;
  @Input() dorpelCode: string;
  @Input() index: number;
  @Input() count: number;
  @Input() schema: string;

  public isSlidingDorpel: boolean;
  public isBuitenDorpel: boolean;
  public isBinnenDorpel: boolean;
  @Output() voorsponningChanged: EventEmitter<any> = new EventEmitter();
  @Output() currentNeutChanged: EventEmitter<any> = new EventEmitter();

  public neuten: NeutDto[] = [];
  public selectedNeut: NeutDto | null = null;
  public neutenCathegories: NeutenCathegory[] = [];
  public activeNeutCathegory: NeutenCathegory | null = null;
  public isCathegoryListOpen: boolean = false;
  public boundClickOutsideListener: any = null;
  private _formComponent: FormComponent;
  private neutenDependentFields: string[] = [
    "neut",
    "neutDescription",
    "linksVoorsponning",
    "rechtsVoorsponning",
  ];
  public voorsponningEnabled: boolean = false;
  public shouldHideLeftVoorsponning: boolean = false;
  public shouldHideRightVoorsponning: boolean = false;
  public isSpecialNeut: boolean = false;
  public specialNeutWidth: number = 0;

  private alreadySubscribedToNeutenCount: boolean = false;
  private defaultSponningmaatValue: number;

  public constructor(
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private uiStateService: UiStateService,
    private graphicsService: GraphicsService,
    public tooltip: TooltipService
  ) {
    super();
  }

  public ngOnInit() {
    this.boundClickOutsideListener = this.clickOutsideListener.bind(this);
    this.neutModel = this.input.model;
    this.neuten = this.input.neutenChoices.filter((neut) =>
      this.neutMatchesDorpelAndPosition(neut, this.dorpelCode)
    );
    if (this.uiStateService.viewMode === "Edit") {
      this.setCurrentDorpelType(this.uiStateService.currentDorpel.dorpelCode);
    }
    this.packNeutenIntoCathegories(this.neuten);

    this.uiStateService.neuten$
      .pipe(filter((neuten) => !!neuten))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((neuten) => {
        let currentNeut = neuten[this.index];

        if (!currentNeut) {
          return;
        }
        
        let prevNeut = neuten[this.index - 1];
        let prevNeutDto = this.uiStateService.getNeutDtoByCodeAndPositie(
          prevNeut?.neutCode,
          prevNeut?.positie
        );
        this.filterOutIncompatibleNeuten(prevNeut?.neutDescription, currentNeut?.neutCode, prevNeutDto?.neutlengte);
        let isLeftVakConversion = this.isConversionVakFollowingNeut(prevNeut?.neutDescription);
        let isRightVakConversion = this.isConversionVakFollowingNeut(currentNeut?.neutDescription);
        this.setVoorsponningOptions(isLeftVakConversion, isRightVakConversion);
        let isFirstNeut = this.index === 0;
        let isLastNeut = this.index === neuten.length - 1;
        if (!!currentNeut.neutDescription && currentNeut.neutCode !== this.input.model.neut) {
          let currentNeutDto: NeutDto = this.uiStateService.getNeutDtoByCodeAndPositie(
            currentNeut.neutCode,
            currentNeut.positie
          );
          let isDraaikiep = currentNeutDto ? NeutUtils.isDraaikiep(currentNeutDto.omschrijving) : false; 
          this.setVoorsponningDefaultValueIfNecessary(isLeftVakConversion, isRightVakConversion, isFirstNeut, isLastNeut, isDraaikiep);
        }
      });
    this.formGroup = this.buildForm();
    this._formComponent = new FormComponent(this.formGroup);
    //on init (for edit mode) we need to check again if the selected neut has voorsponning activated on the DTO level

    this.computeVoorsponningVisibility();

    this.uiStateService.dorpelCode$
      .pipe(
        filter((dorpelCode) => !!dorpelCode),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((dorpelCode) => {
        this.schema = null; //reset schema value on dorpel change as the neut component does not reinit and keeps old value;
        //we only subscribe to sluitpottenCount field once we know it's enabled

        let damLevel = DorpelUtils.getDamLevelFromDorpelCode(dorpelCode);
        this.defaultSponningmaatValue = damLevel + 10;
        //caution: order is important
        this.setCurrentDorpelType(dorpelCode);
        this.subscribeToNeutenCountChanges();
        // this.setDefaultVoorsponningValues(); // !!! this might be reinstated
      });
    this.uiStateService.schema$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((schema) => {
        this.toggleNeutDisablingBySchema(schema);
      });
    this.toggleNeutDisablingBySchema();
  }

  public isConversionVakFollowingNeut(neutDescription: string): boolean {
    /**
     * we usually determined if a neut is tussen by targeting the word tussen in description which was only included in regular type neuten
     * now draaikiep also include work tussen in  description ..so we needed to adjust the rule by excluding dk tussen 
     * 
     * also, all draaikiep are determining a conversion neut
    */
    if (!neutDescription) {
      return;
    }
    let isDraaikiep = neutDescription.toLocaleLowerCase().indexOf("dk") !== -1;
    let isNeutRegularOrW2 =
      neutDescription.toLocaleLowerCase().indexOf("w2") !== -1 ||
      neutDescription.toLocaleLowerCase().indexOf("links") !== -1 ||
      (neutDescription.toLocaleLowerCase().indexOf("tussen") !== -1 && !isDraaikiep)
      
    return this.isBuitenDorpel && (isDraaikiep || !isNeutRegularOrW2);
  }

  private setVoorsponningDefaultValueIfNecessary(
    isLeftVakConversion: boolean,
    isRightVakConversion: boolean,
    isFirstNeut: boolean,
    isLastNeut: boolean,
    isDraaikiep): void {
    //reset values
    this.uiStateService.populateLeftVoorsponning(this.index, null);
    this.uiStateService.populateRightVoorsponning(this.index, null);
    this.formGroup.get("linksVoorsponning").setValue("0");
    this.formGroup.get("rechtsVoorsponning").setValue("0");

    if(isDraaikiep) {
      return;
    }

    if (this.isBuitenDorpel && !isLeftVakConversion && !isFirstNeut) {
      const linksVoorsponning = VOORSPONNING_SIZE_MAP["6x19"];
      this.uiStateService.populateLeftVoorsponning(this.index, linksVoorsponning);
      this.formGroup.get("linksVoorsponning").setValue("6x19");
    }

    if (this.isBuitenDorpel && !isRightVakConversion && !isLastNeut) {
      const rechtsVoorsponning = VOORSPONNING_SIZE_MAP["6x19"];
      this.uiStateService.populateRightVoorsponning(this.index, rechtsVoorsponning);
      this.formGroup.get("rechtsVoorsponning").setValue("6x19");
    }
  }

  public getVorsponningValue(vorsponningSize: VorsponningSize): string {
    return NeutUtils.getVorsponningValue(vorsponningSize);
  }

  private updateNeutenChoices(neuten: NeutDto[], prevNeutDescription: string) {
    if (NeutUtils.isRegular(prevNeutDescription)) {
      this.neuten = neuten.filter((neut) => {
        let description = neut.omschrijving;
        let position = neut.positie;
        return NeutUtils.isRegularTussen(description, position) || NeutUtils.isRegularRight(description, position) || NeutUtils.isW3(description) || NeutUtils.isSpecial(description);
      });
    } else if (NeutUtils.isW1(prevNeutDescription) || NeutUtils.isW5(prevNeutDescription) || NeutUtils.isW3(prevNeutDescription)) {
      this.neuten = neuten.filter((neut) => {
        let description = neut.omschrijving;
        return NeutUtils.isW2(description) || NeutUtils.isW5(description) || NeutUtils.isW4(description) || NeutUtils.isDKMidRight(description) || NeutUtils.isSpecial(description);
      });
    } else if (NeutUtils.isW2(prevNeutDescription)) {
      this.neuten = neuten.filter((neut) => {
        let description = neut.omschrijving;
        return NeutUtils.isRegularTussen(description) || NeutUtils.isRegularRight(description) || NeutUtils.isW3(description);
      });
    } else if (NeutUtils.isDKLeft(prevNeutDescription)) {
      this.neuten = neuten.filter((neut) =>{
        let description = neut.omschrijving;
        return NeutUtils.isDKRight(description) || NeutUtils.isDKMidBoth(description) || NeutUtils.isDKMidLeft(description) || NeutUtils.isSpecial(description);
      });
    } else if (NeutUtils.isDKMidLeft(prevNeutDescription)) {
      this.neuten = neuten.filter((neut) =>{
        let description = neut.omschrijving;
        return NeutUtils.isDKMidRight(description) || NeutUtils.isW2(description) || NeutUtils.isW5(description) || NeutUtils.isW4(description) || NeutUtils.isSpecial(description);
      });
    } else if (NeutUtils.isDKMidRight(prevNeutDescription) || NeutUtils.isDKMidBoth(prevNeutDescription)) {
      this.neuten = neuten.filter((neut) =>{
        let description = neut.omschrijving;
        return NeutUtils.isDKMidLeft(description) || NeutUtils.isDKMidBoth(description) || NeutUtils.isDKRight(description) || NeutUtils.isSpecial(description);
      });
    }
  }

  public filterOutIncompatibleNeuten(prevNeutDescription: string, currentNeutCode: string, prevNeutHeight: string) {
    //TODO: maybe think of a more neat approach when we can identify neut via flags and not description matching
    let neuten = this.input.neutenChoices.filter((neut) =>
      this.neutMatchesDorpelAndPosition(neut, this.dorpelCode)
    );
    if (!prevNeutDescription || !neuten.length) {
      this.neuten = neuten;
      this.neutenCathegories = [];
      this.packNeutenIntoCathegories(this.neuten);
      return;
    }

    this.updateNeutenChoices(neuten, prevNeutDescription);

    // if (this.isSlidingDorpel) {
    //   //for sliding doors, only allow equal neutheights for left and right. we extra filter out neut options
    //   this.neuten = this.neuten.filter((neut) => {
    //     let neutDto = this.uiStateService.getNeutDtoByCodeAndPositie(
    //       neut.neutCode,
    //       neut.positie
    //     );
    //     return neutDto.neutlengte === prevNeutHeight;
    //   })
    // }

    if (currentNeutCode && !this.neuten.find(neut => neut.neutCode === currentNeutCode)) {
      this.customNeutRemovalErrorMessage = INCOMPATIBLE_NEUT_AUTOREMOVED_CUSTOM_MESSAGE;
      this.selectedNeut = null;
      this.neutModel.omschrijving = "";
      this.neutModel.neut = "";
      const cleanNeutOrder: BestellingNeutDto = {
        neutCode: "",
        neutDescription: "",
        isInHouseNeut: null,
      };
      this.uiStateService.populateNeut(cleanNeutOrder, this.index);
      this.resetNeutCodeSelection();
    }
    this.neutenCathegories = [];
    this.packNeutenIntoCathegories(this.neuten);
  }

  public toggleCathegoryList() {
    this.isCathegoryListOpen = !this.isCathegoryListOpen;

    if (this.isCathegoryListOpen) {
      document.body.addEventListener("click", this.boundClickOutsideListener, true);
    } else {
      document.body.removeEventListener("click", this.boundClickOutsideListener, true);
    }
  }

  private clickOutsideListener(e: any) {
    if (!e.target.closest(`.${this.neutSelectorSpecialClassName}`) && !e.target.closest(`.${this.cathegoriesListSpecialClassName}`)) {
      this.closeCathegoryList();
    }
  }

  public closeCathegoryList() {
    this.isCathegoryListOpen = false;
    document.body.removeEventListener("click", this.boundClickOutsideListener, true);
  }

  public selectNeut(selectedNeut: NeutDto) {
    this.selectedNeut = selectedNeut;
    this.formGroup.get("neut").setValue(selectedNeut.neutCode);
    this.formGroup.get("neutDescription").setValue(selectedNeut.omschrijving);
    this.customNeutRemovalErrorMessage = null;

    this.onNeutSelected();
    this.closeCathegoryList();
    this.resetActiveNeutCathegory();
  }

  public activateNeutCathegory(neutCathegory: NeutenCathegory) {
    this.activeNeutCathegory = neutCathegory;
  }

  public resetActiveNeutCathegory(): void {
    this.activeNeutCathegory = null;
  }

  private packNeutenIntoCathegories(neuten: NeutDto[]) {
    let regulier = [];
    let wissel = [];
    let draai = [];
    let special = [];

    neuten.forEach((neut: NeutDto) => {
      if (NeutUtils.isRegular(neut.omschrijving)) {
        regulier.push(neut);
      }

      if (NeutUtils.isWissel(neut.omschrijving)) {
        wissel.push(neut);
      }

      if (NeutUtils.isDraaikiep(neut.omschrijving)) {
        draai.push(neut)
      }

      if (NeutUtils.isSpecial(neut.omschrijving)) {
        special.push(neut)
      }
    });

    if (regulier.length) {
      this.neutenCathegories.push({
        description: "Regulier",
        options: regulier
      });
    }

    if (wissel.length) {
      this.neutenCathegories.push({
        description: "Wissel",
        options: wissel
      });
    }

    if (draai.length) {
      this.neutenCathegories.push({
        description: "Draai/Kiep",
        options: draai
      });
    }

    if (special.length) {
      this.neutenCathegories.push({
        description: "Special",
        options: special
      });
    }
  }

  private setCurrentDorpelType(dorpelCode) {
    const chosenDorpel = this.uiStateService.dorpelChoices.find(
      (d) => d.dorpelCode === dorpelCode
    );
    let currentDorpelType = chosenDorpel.dorpelsoort;

    this.isSlidingDorpel = currentDorpelType === "Hefschuifdorpel";
    this.isBuitenDorpel = currentDorpelType === "Buitendorpel";
    this.isBinnenDorpel = currentDorpelType === "Binnendorpel";
  }

  private setVoorsponningOptions(isLeftVakConversion: boolean, isRightVakConversion: boolean) {
    const isSpecialCaseException = this.uiStateService.currentDorpel.dorpelCode === "IW-BI.4272.B";
    const { leftOptions, rightOptions } = NeutUtils.getVoorsponningOptions(isLeftVakConversion, isRightVakConversion, isSpecialCaseException, this.isBinnenDorpel, this.isBuitenDorpel)
    this.leftVoorsponningOptions = leftOptions;
    this.rightVoorsponningOptions = rightOptions;
  }

  computeVoorsponningVisibility(): void {
    this.shouldHideLeftVoorsponning =
      this.neutModel.position === NeutPosition.Left;
    this.shouldHideRightVoorsponning =
      this.neutModel.position === NeutPosition.Right;
  }

  updateVoorsponningVisibility(): void {
    if (!this.formGroup) {
      //safety check
      return;
    }

    if (this.shouldHideLeftVoorsponning) {
      this.formGroup.controls["linksVoorsponning"].disable();
    }

    if (this.shouldHideRightVoorsponning) {
      this.formGroup.controls["rechtsVoorsponning"].disable();
    }
  }

  toggleNeutDisablingBySchema(schema?: string): void {
    if (!this.isSlidingDorpel) {
      return;
    }

    let neutControl = this.formGroup.controls["neut"];
    let neutDescription = this.formGroup.controls["neutDescription"];
    let hasSchema = !!(this.uiStateService.currentDorpel.schema || schema);

    if (!hasSchema) {
      neutControl.disable();
      neutDescription.disable();
    } else {
      neutControl.enable();
      neutDescription.enable();
    }
  }

  //DEFAULT VOORSPONNING VALUES
  // setDefaultVoorsponningValues(): void {
  //   let linksVoorsponning = this.formGroup.get("linksVoorsponning");
  //   let rechtsVoorsponning = this.formGroup.get("rechtsVoorsponning");

  //   if (this.isBuitenDorpel) {
  //     if(!linksVoorsponning.disabled) {
  //       linksVoorsponning.setValue(DEFAULT_BUITENDORPEL_VOORSPONNING_VALUE);
  //       this.uiStateService.populateLeftVoorsponning(this.index, DEFAULT_BUITENDORPEL_VOORSPONNING_VALUE);
  //     } else {
  //       linksVoorsponning.setValue(null);
  //     }

  //     if(!rechtsVoorsponning.disabled) {
  //       rechtsVoorsponning.setValue(DEFAULT_BUITENDORPEL_VOORSPONNING_VALUE);
  //       this.uiStateService.populateRightVoorsponning(this.index, DEFAULT_BUITENDORPEL_VOORSPONNING_VALUE);
  //     } else {
  //       rechtsVoorsponning.setValue(null);
  //     }
  //   }

  //   this.cdRef.detectChanges();
  // }

  private subscribeToNeutenCountChanges(): void {
    if (this.alreadySubscribedToNeutenCount) {
      return;
    }

    this.uiStateService.neutenCount$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((neutenCount) => {
        let enable = !!neutenCount || neutenCount === 0;
        this.enableNeutenDependentFields(enable);
        // this.setDefaultVoorsponningValues(); //  !!! this might be reinstated
      });
    this.alreadySubscribedToNeutenCount = true;
  }

  private enableNeutenDependentFields(shouldEnable: boolean): void {
    this.voorsponningEnabled = shouldEnable;
    this.neutenDependentFields.forEach((field) => {
      let fieldToUpdate = this.formGroup.controls[field];

      if (fieldToUpdate) {
        if (shouldEnable) {
          if (this.isVoorsponningDisabledCase(fieldToUpdate)) {
            return;
          }
          fieldToUpdate.enable();
        } else {
          fieldToUpdate.disable();
        }
      }
    });

    this.toggleNeutDisablingBySchema();
  }

  isVoorsponningDisabledCase(fieldToUpdate) {
    return (
      (fieldToUpdate === this.formGroup.controls["linksVoorsponning"] &&
        this.neutModel.position === NeutPosition.Left) ||
      (fieldToUpdate === this.formGroup.controls["rechtsVoorsponning"] &&
        this.neutModel.position === NeutPosition.Right)
    );
  }

  public ngOnChanges(param) {
    this.neutModel = this.input.model;
    if (param.dorpelCode) {
      const dorpelCode = param.dorpelCode.currentValue;
      if (dorpelCode) {
        this.neuten = this.input.neutenChoices.filter((neut) =>
          this.neutMatchesDorpelAndPosition(neut, this.dorpelCode)
        );
        if (param.dorpelCode.previousValue) {
          this.resetNeutCodeSelection();
        }
        this.currentlySelectedNeut = null;
      }
    }
    if (param.count) {
      this.neuten = this.input.neutenChoices.filter((neut) =>
        this.neutMatchesDorpelAndPosition(neut, this.dorpelCode)
      );
      if (this.formGroup) {
        const value = this.formGroup.get("neut").value;
        if (
          value &&
          this.neuten.length > 0 &&
          this.neuten.findIndex((n) => n.neutCode === value) < 0
        ) {
          this.resetNeutCodeSelection();
        }
      }
      this.computeVoorsponningVisibility();
      this.updateVoorsponningVisibility();
    }

    // FILTER BY SCHEMA
    // if (param.schema) {
    //   this.neuten = this.input.neutenChoices.filter(neut => this.neutMatchesDorpelAndPosition(neut, this.dorpelCode))
    //     .filter(neut => this.neutMatchesSchema(neut, this.schema));
    //   if (param.schema.previousValue) {
    //     this.resetNeutCodeSelection('');
    //   }
    // }

    this.cdRef.detectChanges();
  }

  public get label(): string {
    return NeutUtils.getLabel(this.index, this.count, this.isSlidingDorpel);
  }

  public get neutSelectorSpecialClassName(): string {
    return `neutSelector${this.index}`;
  }

  public get cathegoriesListSpecialClassName(): string {
    return `cathegoriesList${this.index}`;
  }


  public get isVoorsponningActive(): boolean {
    let selectedNeut: NeutDto = this.getSelectedNeut();
    return !!selectedNeut && !NeutUtils.isSpecial(selectedNeut.omschrijving) && !this.isSlidingDorpel && (this.uiStateService.currentProductType === "Luvema" || this.uiStateService.currentProductType === "Isowel")
  }


  onLinksVoorsponningChanged(): void {
    const linksVoorsponning = VOORSPONNING_SIZE_MAP[this.formGroup.get("linksVoorsponning").value];
    //tell parent component to set same value on the mirrored vorsponning of the previous neut
    this.voorsponningChanged.emit({
      targetProperty: "rechtsVoorsponning",
      value: linksVoorsponning,
      targetNeutIndex: this.index - 1,
    });
    this.uiStateService.populateLeftVoorsponning(this.index, linksVoorsponning);
    if (this.uiStateService.currentDorpel.zaagmaat) {
      this.uiStateService.triggerRedraw();
    }
  }

  onRechtsVoorsponningChanged(): void {
    const rechtsVoorsponning = VOORSPONNING_SIZE_MAP[this.formGroup.get("rechtsVoorsponning").value];
    //tell parent component to set same value on the mirrored vorsponning of the next neut
    this.voorsponningChanged.emit({
      targetProperty: "linksVoorsponning",
      value: rechtsVoorsponning,
      targetNeutIndex: this.index + 1,
    });
    this.uiStateService.populateRightVoorsponning(
      this.index,
      rechtsVoorsponning
    );
    if (this.uiStateService.currentDorpel.zaagmaat) {
      this.uiStateService.triggerRedraw();
    }
  }

  setLinksVoorsponningValue(voorsponningSize: VorsponningSize): void {
    //method called by parent component to mirror-match vorsponning values
    if (this.formGroup.get("position").value === NeutPosition.Left) {
      return;
    }
    this.formGroup.get("linksVoorsponning").setValue(NeutUtils.getVorsponningValue(voorsponningSize) || "0");
    this.uiStateService.populateLeftVoorsponning(this.index, voorsponningSize);
  }

  setRechtsVoorsponningValue(voorsponningSize: VorsponningSize): void {
    //method called by parent component to mirror-match vorsponning values
    if (this.formGroup.get("position").value === NeutPosition.Right) {
      return;
    }
    this.formGroup.get("rechtsVoorsponning").setValue(NeutUtils.getVorsponningValue(voorsponningSize) || "0");
    this.uiStateService.populateRightVoorsponning(this.index, voorsponningSize);
  }

  public getSelectedNeut(): NeutDto {
    if (!this.formGroup) {
      return null;
    }
    const selectedNeutCode = this.formGroup.get("neut");
    if (!selectedNeutCode) {
      return null;
    }
    return this.neuten.find((n) => n.neutCode === selectedNeutCode.value);
  }

  public onSpecialNeutCustomWidthChange(event: any) {
    let customWidth = event.target.value ? parseInt(event.target.value) : null;
    this.specialNeutWidth = customWidth;
    this.uiStateService.populateNeut({customWidth: customWidth}, this.index);
    this.uiStateService.updateSpecialNeutCustomWidth(customWidth);
  }

  public onNeutSelected() {
    //TODO: check this

    const neut: NeutDto = this.getSelectedNeut();
    const neutOrder: BestellingNeutDto = {
      neutCode: neut.neutCode,
      neutDescription: neut.omschrijving,
      positie: neut.positie,
      isInHouseNeut: neut.isInHouseNeut,
      //reset daagmaat conditional values:
      aanslagprofiel: undefined,
      beglazingsprofiel: undefined,
      glaslat: undefined,
      gemonteerd: undefined,
      damverbreeding: undefined,
      dubbelAanslagprofiel: undefined,
      customWidth: undefined
    };
    this.currentlySelectedNeut = neutOrder;
    
    this.isSpecialNeut = neut ? NeutUtils.isSpecial(neut.omschrijving) : false;
    if (this.isSpecialNeut) {
      this.specialNeutWidth = parseInt(neut?.neutbreedte) || 0;
      neutOrder.customWidth = this.specialNeutWidth;
    }

    this.uiStateService.populateNeut(neutOrder, this.index);
    this.currentNeutChanged.emit(neutOrder);
  }

  public showNeutTooltip(target: HTMLElement) {
    let currentNeutSponningmaat = this.uiStateService.currentDorpel.neuten[this.index].sponningmaat;
    let sponningmaatValue = currentNeutSponningmaat || this.defaultSponningmaatValue;
    let neutCode = this.currentlySelectedNeut?.neutCode;

    if (neutCode) {
      //we have a selected neut so we show a custom tooltip
      if (neutCode.indexOf(".W") === -1) {
        //if not wissel neut, even if we have the sponningmaat value set for the current dorpel, we don't use that
        //in determining the file name
        sponningmaatValue = null;
      }

      let neutImageName = this.getCustomNeutTooltipImageName(
        neutCode,
        sponningmaatValue
      );
      let loadImageTooltip = () => {
        this.isTooltipResourceCheckInProgress = false;

        if (this.shouldAbortTooltipDisplay) {
          this.shouldAbortTooltipDisplay = false;
          return;
        }

        this.tooltip.showTooltip(target, {
          customImageName: neutImageName,
          imageOnly: true,
        });
      };
      let loadMissingImageTooltip = () => {
        this.isTooltipResourceCheckInProgress = false;

        if (this.shouldAbortTooltipDisplay) {
          this.shouldAbortTooltipDisplay = false;
          return;
        }

        this.tooltip.showTooltip(target, {
          customTextDescription: MISSING_NEUT_IMAGE_TEXT,
          descriptionOnly: true,
        });
      };
      this.attemptResourceLoad(
        `${this.tooltip.assetsPath}${neutImageName}`,
        loadImageTooltip,
        loadMissingImageTooltip
      );
    } else {
      //we show the default tooltip (no neut selection)
      this.tooltip.showTooltip(target);
    }
  }

  public hideNeutTooltip(): void {
    if (this.isTooltipResourceCheckInProgress) {
      this.shouldAbortTooltipDisplay = true;
    }
    this.tooltip.hideTooltip();
  }

  private attemptResourceLoad(
    resourcePath: string,
    loadCallback,
    failCallback
  ): void {
    this.shouldAbortTooltipDisplay = false;
    this.isTooltipResourceCheckInProgress = true;
    let img = new Image();
    img.onload = loadCallback;
    img.onerror = failCallback;
    img.src = resourcePath;
  }

  private getCustomNeutTooltipImageName(
    neutCode: string,
    sponningmaatValue?: number
  ): string {
    return (
      `${neutCode}` +
      (sponningmaatValue ? `-S${sponningmaatValue}.png` : `.png`)
    );
  }

  public resetNeutCodeSelection() {
    if (!this.formGroup) {
      return;
    }
    this.formGroup.get("neut").setValue("");
    this.formGroup.get("neutDescription").setValue("");
  }

  public showError(key: string): boolean {
    return this._formComponent.showError(key);
  }

  public errorMessage(key: string): string {
    const formControl = this.formGroup.get(key);
    if (formControl.valid || formControl.disabled) {
      return "";
    }
    const [firstError] = Object.keys(formControl.errors);
    const error = ValidationMessages[key]
      ? ValidationMessages[key][firstError]
      : null;
    return error || this._formComponent.errorMessage(key);
  }

  private neutMatchesDorpelAndPosition(
    neut: NeutDto,
    dorpelCode: string
  ): boolean {
    if (!dorpelCode) {
      return false;
    }

    if (neut.dorpelCode !== dorpelCode) {
      return false;
    }

    return (
      neut.positie === NeutUtils.getPositionString(this.neutModel.position)
    );
  }

  // FILTER BY SCHEMA
  // private neutMatchesSchema(neut: NeutDto, schema: string): boolean {
  //   return neut.schema.indexOf(schema) !== -1;
  // }

  private buildForm(): FormGroup {
    const formGroup = this.formBuilder.group({
      neut: [
        { value: this.neutModel?.neut || "", disabled: true },
        [Validators.required],
      ],
      position: this.neutModel.position,
      linksVoorsponning: [
        {
          value: NeutUtils.getVorsponningValue(this.neutModel?.linksVoorsponning) || "0",
          disabled: !this.voorsponningEnabled,
        },
      ],
      rechtsVoorsponning: [
        {
          value: NeutUtils.getVorsponningValue(this.neutModel?.rechtsVoorsponning) || "0",
          disabled: !this.voorsponningEnabled,
        },
      ],
      neutDescription: [
        { value: this.neutModel?.omschrijving || "", disabled: true },
        [Validators.required],
      ]
    });

    return formGroup;
  }
}

const ValidationMessages = {
  neut: {
    required: "Selecteer een waarde a.u.b.",
  },
  maatA: {
    required: "Selecteer een waarde a.u.b.",
  },
  sponningsoort: {
    required: "Selecteer een waarde a.u.b.",
  },
  maatZ: {
    required: "Selecteer een waarde a.u.b.",
  },
};
